import { SiteNav } from "@/components/site-nav";
import { SiteFooter } from "@/components/site-footer";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { CheckCircle2, AlertCircle, Clock, Zap } from "lucide-react";

interface StatusIndicatorProps {
  name: string;
  status: 'operational' | 'degraded' | 'outage' | 'maintenance';
  description: string;
}

function StatusIndicator({ name, status, description }: StatusIndicatorProps) {
  const configs = {
    operational: { color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', icon: CheckCircle2, label: 'Operational' },
    degraded: { color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20', icon: Clock, label: 'Degraded' },
    outage: { color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20', icon: AlertCircle, label: 'Outage' },
    maintenance: { color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20', icon: Zap, label: 'Maintenance' },
  };

  const config = configs[status];
  const Icon = config.icon;

  return (
    <Card className="bg-white/5 border-white/10 hover-elevate transition-all overflow-hidden">
      <CardHeader className="flex flex-row items-center justify-between p-6 space-y-0">
        <h3 className="text-lg font-bold text-white">{name}</h3>
        <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full border ${config.bg} ${config.border} ${config.color}`}>
          <Icon size={14} />
          <span className="text-[10px] font-bold uppercase tracking-widest">{config.label}</span>
        </div>
      </CardHeader>
      <CardContent className="px-6 pb-6">
        <p className="text-sm text-white/40 leading-relaxed">{description}</p>
      </CardContent>
    </Card>
  );
}

export default function Status() {
  return (
    <div className="min-h-screen bg-[#030308] flex flex-col">
      <SiteNav />
      
      <main className="flex-1 py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <header className="mb-16 text-center">
            <h1 className="text-4xl md:text-6xl font-display font-black text-white mb-6 tracking-tight">
              Service <span className="text-emerald-400">Status</span>
            </h1>
            <p className="text-white/40 max-w-2xl mx-auto text-lg leading-relaxed">
              Real-time monitoring of our core infrastructure, AI systems, and external integrations.
            </p>
          </header>

          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-3xl p-8 mb-12 flex items-center justify-center gap-4 text-center">
            <div className="w-4 h-4 rounded-full bg-emerald-400 animate-pulse shadow-[0_0_15px_rgba(52,211,153,0.5)]" />
            <span className="text-xl font-bold text-emerald-400">All Systems Operational</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="status-grid">
            <StatusIndicator 
              name="API Core" 
              status="operational" 
              description="Primary API infrastructure responsible for handling agent logic and workspace data."
            />
            <StatusIndicator 
              name="Main Database" 
              status="operational" 
              description="High-availability PostgreSQL cluster with real-time replication and continuous backups."
            />
            <StatusIndicator 
              name="AI Inference (Peggy/Draper)" 
              status="operational" 
              description="Agentic reasoning engine powered by our distributed LLM network."
            />
            <StatusIndicator 
              name="Stripe Payments" 
              status="operational" 
              description="Subscription processing, credit management, and billing infrastructure."
            />
            <StatusIndicator 
              name="Social Auth (Google/Replit)" 
              status="operational" 
              description="User authentication and secure session management systems."
            />
            <StatusIndicator 
              name="Asset Storage" 
              status="operational" 
              description="Secure media storage and content delivery network for workspace assets."
            />
          </div>

          <div className="mt-20 pt-12 border-t border-white/5 text-center">
            <h4 className="text-sm font-bold text-white mb-4 uppercase tracking-wider">Uptime History</h4>
            <div className="flex gap-1 justify-center h-8">
              {[...Array(90)].map((_, i) => (
                <div key={i} className="flex-1 max-w-[4px] bg-emerald-400/30 rounded-full hover:bg-emerald-400 transition-colors cursor-help" title={`Day ${90-i}: 100%`} />
              ))}
            </div>
            <div className="flex justify-between mt-4 text-[10px] font-bold uppercase tracking-widest text-white/20">
              <span>90 Days Ago</span>
              <span>100% Uptime</span>
              <span>Today</span>
            </div>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
